import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { UIProvider } from '@kidova/ui-web';
import { I18nProvider } from '../i18n';
import { loadLocale } from '../i18n/load';
import { afterAppReady } from '../lib/hydration';
import {
  applyLocale,
  applyTheme,
  currentLocale,
  resolvedTheme,
  themeChoice,
  type AppLocale,
  type ThemeChoice,
} from '../lib/preferences';

interface Preferences {
  locale: AppLocale;
  theme: ThemeChoice;
  setLocale: (locale: AppLocale) => void;
  setTheme: (theme: ThemeChoice) => void;
}

const PreferencesContext = createContext<Preferences | null>(null);

export function usePreferences(): Preferences {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error('usePreferences outside Providers');
  return value;
}

// Toasts only follow user actions, so their UI (and Radix Toast) loads after the first paint; toast()
// calls made before that are queued and shown once it is there.
const Toaster = lazy(() => import('@kidova/ui-web/toaster').then((m) => ({ default: m.Toaster })));

function DeferredToaster() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    afterAppReady(() => {
      setReady(true);
    });
  }, []);
  return ready ? (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  ) : null;
}

export function Providers({
  client,
  children,
  locale: initialLocale,
}: {
  client: QueryClient;
  children: ReactNode;
  /** Set when prerendering, where there is no <html> to read the language from. */
  locale?: AppLocale;
}) {
  const [locale, setLocaleState] = useState<AppLocale>(() => initialLocale ?? currentLocale());
  const [theme, setThemeState] = useState<ThemeChoice>(themeChoice);

  const setLocale = useCallback((next: AppLocale) => {
    void loadLocale(next).then(() => {
      applyLocale(next);
      setLocaleState(next);
    });
  }, []);
  const setTheme = useCallback((next: ThemeChoice) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  // "System" follows the OS live, without a reload.
  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => {
      document.documentElement.setAttribute('data-theme', resolvedTheme('system'));
    };
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
    };
  }, [theme]);

  const preferences = useMemo(
    () => ({ locale, theme, setLocale, setTheme }),
    [locale, theme, setLocale, setTheme],
  );
  return (
    <PreferencesContext.Provider value={preferences}>
      <QueryClientProvider client={client}>
        <UIProvider locale={locale}>
          <I18nProvider locale={locale}>
            {children}
            <DeferredToaster />
          </I18nProvider>
        </UIProvider>
      </QueryClientProvider>
    </PreferencesContext.Provider>
  );
}
